/**
 * [TD-47.3] QueueButton - small +queue / -queue toggle attached to player
 * rows in the snake left-pane player pool. Mounts via the new
 * `renderRowAddon` prop on PlayerPoolWithDrawer (Gap 2 verdict A).
 *
 * Two visual states:
 *   • Not queued - outline plus icon, hover orange
 *   • Queued    - filled green check (cue: "this player is in your queue")
 *
 * Click stops propagation so it does NOT trigger the row's drawer-open
 * handler in PlayerPoolWithDrawer.
 */
import { Plus, Check } from "lucide-react";
import { useDraftQueueContext } from "../../contexts/DraftQueueContext";

export default function QueueButton({ player }) {
  const { addToQueue, removeFromQueue, isQueued, queueCapped } =
    useDraftQueueContext();
  if (!player?.id) return null;

  const queued = isQueued(player.id);
  const disabled = !queued && queueCapped;

  const handleClick = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (queued) removeFromQueue(player.id);
    else addToQueue(player);
  };

  const label = queued
    ? `Remove ${player.name} from queue`
    : disabled
      ? "Queue full (100 max)"
      : `Add ${player.name} to queue`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={`queue-btn-${player.id}`}
      data-queued={queued ? "true" : undefined}
      className={`shrink-0 w-6 h-6 rounded-md border flex items-center justify-center transition-all ${
        queued
          ? "bg-[#00C853]/15 border-[#00C853]/50 text-[#00C853] hover:bg-[#00C853]/25"
          : disabled
            ? "bg-[#1A1A1A] border-[#252525] text-[rgba(255,255,255,0.20)] cursor-not-allowed"
            : "bg-[#1C1C1C] border-[#2E2E2E] text-[rgba(255,255,255,0.45)] hover:border-[#FF6B00]/60 hover:text-[#FF6B00]"
      }`}
    >
      {queued ? <Check size={12} /> : <Plus size={12} />}
    </button>
  );
}
